import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonMenuButton,
  IonIcon
} from '@ionic/react';
import { mailOutline, callOutline, businessOutline, ribbonOutline, calendarOutline, shieldCheckmarkOutline, alertCircleOutline, giftOutline, logoWhatsapp } from 'ionicons/icons';
import QRCode from 'qrcode';
import { connect } from '../data/connect';
import { RouteComponentProps } from 'react-router';
import UserAvatar from '../components/UserAvatar';
import UserProfileService from '../services/UserProfileService';
import { setUsername, setEmail, setPhone, setCompany, setPosition } from '../data/user/user.actions';
import { getDefaultAvatar } from '../utils/defaultAvatar';
import { getAppConfig } from '../config/app.config';
import './CarnetDigital.scss';

interface OwnProps extends RouteComponentProps {}

interface StateProps {
  username?: string;
  userced?: string;
  userid?: string;
  userother?: string;
  eventcode?: string;
  eventName?: string;
  email?: string;
  phone?: string;
  company?: string;
  position?: string;
  profilePhotoUrl?: string;
}

interface DispatchProps {
  setUsername: typeof setUsername;
  setEmail: typeof setEmail;
  setPhone: typeof setPhone;
  setCompany: typeof setCompany;
  setPosition: typeof setPosition;
}

interface CarnetDigitalProps extends OwnProps, StateProps, DispatchProps {}

const CarnetDigital: React.FC<CarnetDigitalProps> = ({
  username,
  userced,
  userother,
  eventcode,
  eventName,
  email,
  phone,
  company,
  position,
  profilePhotoUrl,
  setUsername: setUsernameAction,
  setEmail,
  setPhone,
  setCompany,
  setPosition
}) => {
  const [photoUrl, setPhotoUrl] = useState<string>('');
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [membershipExpiry, setMembershipExpiry] = useState<string>('');
  const [isMemberActive, setIsMemberActive] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  // Extract role and company from userother (format: "company&&&role")
  const userCompany = userother?.split('&&&')[0] || company || '';
  const userRole = userother?.split('&&&')[1] || position || '';

  // Determine theme based on role
  const isEstudiante = userRole.toLowerCase().includes('estudiante');
  const theme = isEstudiante ? 'estudiante' : 'profesional';

  const userProfileService = UserProfileService.getInstance();

  // Use mandatory event code from config, fallback to user's current event
  const carnetEventCode = getAppConfig().features.carnetDigital.mandatoryEventCode || eventcode || '';

  // Build verification URL for QR
  const verificationUrl = `https://www.esimposio.com/e/${carnetEventCode}/${userced || ''}`;

  useEffect(() => {
    if (userced && carnetEventCode) {
      loadProfile();
    } else {
      setLoading(false);
      setHasError(true);
    }
  }, [userced, carnetEventCode]);

  // Generate QR code when carnetEventCode changes
  useEffect(() => {
    if (carnetEventCode && userced) {
      generateQRCode();
    }
  }, [carnetEventCode, userced]);

  const generateQRCode = async () => {
    try {
      const qrImage = await QRCode.toDataURL(verificationUrl, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrDataUrl(qrImage);
    } catch (error) {
      console.error('CarnetDigital: Error al generar QR:', error);
    }
  };

  const loadProfile = async () => {
    if (!userced || !carnetEventCode) {
      setLoading(false);
      setHasError(true);
      return;
    }
    setLoading(true);
    setHasError(false);
    try {
      const profile = await userProfileService.getCurrentUserProfile(userced, carnetEventCode);
      if (profile) {
        if (profile.email) setEmail(profile.email);
        if (profile.phone) setPhone(profile.phone);
        if (profile.company) setCompany(profile.company);
        if (profile.position) setPosition(profile.position);
        if (profile.profilePhotoUrl) setPhotoUrl(profile.profilePhotoUrl);
      }

      // Try to load membership data from Firebase user record
      try {
        const { firebase } = await import('../config/firebase.config');
        const usersSnapshot = await firebase.database().ref('/user').once('value');
        if (usersSnapshot.exists()) {
          const allUsers = usersSnapshot.val();
          for (const userId in allUsers) {
            const userData = allUsers[userId];
            if (userData.ced === userced && userData.eventcode === carnetEventCode) {
              if (userData.membershipExpiry) {
                setMembershipExpiry(userData.membershipExpiry);
                const expiryDate = new Date(userData.membershipExpiry);
                setIsMemberActive(expiryDate >= new Date());
              }
              if (userData.memberActive !== undefined) {
                setIsMemberActive(userData.memberActive);
              }
              break;
            }
          }
        }
      } catch (fbError) {
        console.warn('CarnetDigital: No se pudo cargar datos de membresía:', fbError);
      }
    } catch (error) {
      console.error('CarnetDigital: Error al cargar perfil:', error);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  const displayPhoto = photoUrl || profilePhotoUrl || getDefaultAvatar();

  return (
    <IonPage id="carnet-digital-page">
      <IonHeader>
        <IonToolbar className="transparent-toolbar">
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Carnet Digital</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {loading ? (
          <div className="carnet-no-data">
            <p style={{ color: '#6b7280', fontSize: '16px' }}>Cargando información...</p>
          </div>
        ) : hasError || (!userced && !carnetEventCode) ? (
          <div className="carnet-no-data">
            <IonIcon icon={alertCircleOutline} style={{ fontSize: '64px', color: '#f59e0b', marginBottom: '16px' }} />
            <h3 style={{ color: '#1f2937', margin: '0 0 8px', fontSize: '20px', fontWeight: 600 }}>Información no disponible</h3>
            <p style={{ color: '#6b7280', margin: 0, fontSize: '14px', textAlign: 'center', maxWidth: '300px' }}>
              No se encontró información del evento configurado para el carnet digital. Verifica que el evento tenga la información necesaria.
            </p>
          </div>
        ) : (
        <div className="carnet-container">
          <div className={`carnet-card theme-${theme}`}>
            {/* Header with logo and org name */}
            <div className={`carnet-header theme-${theme}`}>
              <div className={`carnet-header-shapes theme-${theme}`}>
                <div className="shape shape-1" />
                <div className="shape shape-2" />
                <div className="shape shape-3" />
              </div>
              <div className="carnet-logo-row">
                <span className="carnet-logo-text">APII</span>
                <span className="carnet-org-name">
                  ASOCIACIÓN PANAMEÑA<br />DE INGENIEROS INDUSTRIALES
                </span>
              </div>
            </div>

            {/* Photo */}
            <div className="carnet-photo-wrapper">
              <div className={`carnet-photo-frame theme-${theme}`}>
                <div className="carnet-photo-inner">
                  <img src={displayPhoto} alt={username || 'Usuario'} />
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="carnet-body">
              <div className={`carnet-body-shapes theme-${theme}`}>
                <div className="body-shape bs-1" />
                <div className="body-shape bs-2" />
                <div className="body-shape bs-3" />
                <div className="body-shape bs-4" />
                <div className="body-shape bs-5" />
              </div>

              {/* User name & company */}
              <h2 className={`carnet-user-name theme-${theme}`}>
                {username || 'Usuario'}
              </h2>
              <p className="carnet-user-company">
                {userCompany || 'Participante'}
              </p>

              {/* Role badge */}
              <div className="carnet-role-badge">
                <span className={`carnet-role-tag theme-${theme}`}>
                  {userRole || 'Participante'}
                </span>
              </div>

              {/* Membership status badge */}
              <div className="carnet-membership-status">
                <span className={`carnet-status-tag ${isMemberActive ? 'active' : 'inactive'}`}>
                  <IonIcon icon={shieldCheckmarkOutline} />
                  {isMemberActive ? 'Miembro Activo' : 'Miembro Inactivo'}
                </span>
              </div>

              {/* Info fields */}
              <div className="carnet-info-section">
                {email && (
                  <div className="carnet-info-field">
                    <IonIcon icon={mailOutline} className={`carnet-info-icon theme-${theme}`} />
                    <div>
                      <p className="carnet-info-label">Correo</p>
                      <p className="carnet-info-value">{email}</p>
                    </div>
                  </div>
                )}
                {phone && (
                  <div className="carnet-info-field">
                    <IonIcon icon={callOutline} className={`carnet-info-icon theme-${theme}`} />
                    <div>
                      <p className="carnet-info-label">Teléfono</p>
                      <p className="carnet-info-value">{phone}</p>
                    </div>
                  </div>
                )}
                {userCompany && (
                  <div className="carnet-info-field">
                    <IonIcon icon={businessOutline} className={`carnet-info-icon theme-${theme}`} />
                    <div>
                      <p className="carnet-info-label">Empresa</p>
                      <p className="carnet-info-value">{userCompany}</p>
                    </div>
                  </div>
                )}
                {position && (
                  <div className="carnet-info-field">
                    <IonIcon icon={ribbonOutline} className={`carnet-info-icon theme-${theme}`} />
                    <div>
                      <p className="carnet-info-label">Cargo</p>
                      <p className="carnet-info-value">{position}</p>
                    </div>
                  </div>
                )}
                {/* Membership expiry */}
                <div className="carnet-info-field">
                  <IonIcon icon={calendarOutline} className={`carnet-info-icon theme-${theme}`} />
                  <div>
                    <p className="carnet-info-label">Caducidad Membresía</p>
                    <p className="carnet-info-value">
                      {membershipExpiry
                        ? new Date(membershipExpiry).toLocaleDateString('es-PA', { year: 'numeric', month: 'long', day: 'numeric' })
                        : 'No definida'}
                    </p>
                  </div>
                </div>
              </div>

              {/* QR Code */}
              <div className="carnet-qr-section">
                <p className="carnet-qr-label">Verificar Carnet</p>
                {qrDataUrl && (
                  <div className="carnet-qr-container">
                    <img src={qrDataUrl} alt="QR Verificación" className="carnet-qr-image" />
                  </div>
                )}
                <p className="carnet-qr-url">{verificationUrl}</p>
              </div>

              {/* Beneficios & WhatsApp buttons */}
              <div className="carnet-benefits-section">
                <button
                  className={`carnet-benefits-btn theme-${theme}`}
                  onClick={() => window.open(`https://www.esimposio.com/e/${carnetEventCode}/beneficios`, '_blank')}
                >
                  <IonIcon icon={giftOutline} />
                  Beneficios
                </button>
                <button
                  className="carnet-benefits-btn whatsapp"
                  onClick={() => window.open('https://wa.me/50760620131', '_blank')}
                >
                  <IonIcon icon={logoWhatsapp} />
                  Asistencia APII
                </button>
              </div>

              {/* Event name */}
              {eventName && (
                <div className="carnet-event-name">
                  <p className={`theme-${theme}`}>{eventName}</p>
                </div>
              )}
            </div>

            {/* Bottom accent bar */}
            <div className={`carnet-bottom-bar theme-${theme}`} />
          </div>
        </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default connect<OwnProps, StateProps, DispatchProps>({
  mapStateToProps: (state) => ({
    username: state.user.username,
    userced: state.user.userced,
    userid: state.user.userid,
    userother: state.user.userother,
    eventcode: state.user.eventcode,
    eventName: state.user.eventName,
    email: state.user.email,
    phone: state.user.phone,
    company: state.user.company,
    position: state.user.position,
    profilePhotoUrl: state.user.profilePhotoUrl
  }),
  mapDispatchToProps: {
    setUsername,
    setEmail,
    setPhone,
    setCompany,
    setPosition
  },
  component: CarnetDigital
});
